import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import {
  DocumentTextIcon,
  DocumentIcon,
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  DocumentPlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ShieldCheckIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
  CurrencyEuroIcon,
  ListBulletIcon,
  Squares2X2Icon,
  PhotoIcon,
  TableCellsIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import useDocumentManagement from '../hooks/useDocumentManagement';

import DocumentDetailModal from '../components/modals/DocumentDetailModal';
import DocumentAccessModal from '../components/modals/DocumentAccessModal';
import DocumentViewer from '../components/DocumentViewer';
import QuickBookingModal from '../components/modals/QuickBookingModal';
import ConfirmationModal from '../components/ConfirmationModal';

const DocumentManagement: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [isMergeMode, setIsMergeMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [docsToDelete, setDocsToDelete] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageLoadingStates, setImageLoadingStates] = useState<Set<number>>(new Set());
  const [imageErrorStates, setImageErrorStates] = useState<Set<number>>(new Set());
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [quickBookingDoc, setQuickBookingDoc] = useState<any>(null);
  const [viewerDoc, setViewerDoc] = useState<{ id: number; name: string; file_path: string } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant?: 'single' | 'two';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    variant: 'single'
  });
  const [processingDocId, setProcessingDocId] = useState<number | null>(null);
  const [thumbnailVersion, setThumbnailVersion] = useState<number>(Date.now());
  const [filterCreator, setFilterCreator] = useState<string>('all');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const documentsPerPage = viewMode === 'list' ? 25 : 12;

  const {
    documents,
    filteredDocs,
    searchTerm,
    setSearchTerm,
    availableTags,
    isAskingAI,
    selectedDocs,
    setSelectedDocs,
    filterTags,
    pdfError,
    setPdfError,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filterType,
    setFilterType,
    handleFileUpload,
    handleAddTag,
    handleDeleteTag,
    handleDeleteDocument,
    handleDeleteMultiple,
    handleMergeDocuments,
    handleFilterTagToggle,
    handleRemoveFilterTag,
    sendToAI: sendToAIFromHook,
    downloadDocument,
    formatSize,
    truncateText,
    getTagCount,
    fetchDocuments,
  } = useDocumentManagement(user?.mg_nr || 'unknown');

  // AI-Analyse mit Fehlerbehandlung und ConfirmationModal
  const sendToAI = async (docId: number) => {
    setProcessingDocId(docId);
    try {
      const result = await sendToAIFromHook(docId);
      if (result.success) {
        setConfirmModal({ isOpen: true, type: 'success', title: 'AI-Analyse erfolgreich', message: 'Das Dokument wurde erfolgreich analysiert.', variant: 'single' });
      } else {
        setConfirmModal({ isOpen: true, type: 'error', title: 'AI-Analyse fehlgeschlagen', message: result.message || 'Ein unbekannter Fehler ist aufgetreten.', variant: 'single' });
      }
    } finally {
      setProcessingDocId(null);
    }
  };

  // Unique creators + counts
  const uniqueCreators = Array.from(new Set(documents.map(doc => doc.member_name).filter(Boolean)));
  const getCreatorCount = (name: string) => documents.filter(d => d.member_name === name).length;

  // Additional filtering
  const additionalFilteredDocs = filteredDocs.filter(doc => {
    if (filterCreator !== 'all' && doc.member_name !== filterCreator) return false;
    if (filterDateFrom) {
      if (new Date(doc.created_at) < new Date(filterDateFrom)) return false;
    }
    if (filterDateTo) {
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59, 999);
      if (new Date(doc.created_at) > toDate) return false;
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(additionalFilteredDocs.length / documentsPerPage);
  const startIndex = (currentPage - 1) * documentsPerPage;
  const paginatedDocs = additionalFilteredDocs.slice(startIndex, startIndex + documentsPerPage);

  // Stats
  const pdfCount = documents.filter(d => /\.pdf$/i.test(d.file_path)).length;
  const imageCount = documents.filter(d => /\.(jpg|jpeg|png|gif|webp)$/i.test(d.file_path)).length;

  // Diese Woche (letzte 7 Tage)
  const thisWeekCount = useMemo(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return documents.filter(d => new Date(d.created_at) >= sevenDaysAgo).length;
  }, [documents]);

  // Unkategorisierte (ohne tag)
  const uncategorizedDocs = useMemo(
    () => documents.filter(d => !d.tag || d.tag.trim() === ''),
    [documents]
  );
  const uncategorizedCount = uncategorizedDocs.length;

  // Active filter count
  const activeFiltersCount =
    filterTags.length +
    (filterType !== 'all' ? 1 : 0) +
    (filterCreator !== 'all' ? 1 : 0) +
    (filterDateFrom ? 1 : 0) +
    (filterDateTo ? 1 : 0);

  const hasActiveFilters = activeFiltersCount > 0 || searchTerm;

  const clearAllFilters = () => {
    setSearchTerm('');
    filterTags.forEach(tag => handleRemoveFilterTag(tag));
    setFilterType('all');
    setFilterCreator('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterTags, filterType, filterCreator, filterDateFrom, filterDateTo]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsUploading(true);
      setUploadProgress({ current: 0, total: e.target.files.length });
      try {
        await handleFileUpload(e.target.files);
        setConfirmModal({ isOpen: true, type: 'success', title: 'Upload erfolgreich', message: `${e.target.files.length} Dokument${e.target.files.length > 1 ? 'e' : ''} erfolgreich hochgeladen.`, variant: 'single' });
      } catch (error) {
        setConfirmModal({ isOpen: true, type: 'error', title: 'Upload fehlgeschlagen', message: 'Ein oder mehrere Dokumente konnten nicht hochgeladen werden.', variant: 'single' });
      } finally {
        setIsUploading(false);
        setUploadProgress({ current: 0, total: 0 });
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  const toggleDocSelection = (docId: number) => {
    setSelectedDocs(prev => prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]);
  };

  const handleMerge = async () => {
    if (selectedDocs.length < 2) {
      setPdfError('Mindestens 2 Dokumente auswählen');
      setTimeout(() => setPdfError(null), 3000);
      return;
    }
    const newDocId = await handleMergeDocuments(selectedDocs);
    if (newDocId) {
      setIsMergeMode(false);
      setSelectedDocs([]);
    }
  };

  const confirmDelete = () => {
    if (docsToDelete.length > 0) {
      handleDeleteMultiple(docsToDelete);
      setDocsToDelete([]);
      setShowDeleteConfirm(false);
      setSelectedDocs([]);
    }
  };

  const handleImageLoad = (docId: number) => {
    setImageLoadingStates(prev => { const s = new Set(prev); s.delete(docId); return s; });
    setImageErrorStates(prev => { const s = new Set(prev); s.delete(docId); return s; });
  };

  const handleImageError = (docId: number) => {
    setImageLoadingStates(prev => { const s = new Set(prev); s.delete(docId); return s; });
    setImageErrorStates(prev => new Set(prev).add(docId));
  };

  const getSecureImageUrl = (docId: number) => {
    const token = localStorage.getItem('jwt_token');
    const baseUrl = BASE_URL || localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';
    return `${baseUrl}/api/documents/download-url/${docId}?thumbnail=true&token=${token || ''}&v=${thumbnailVersion}`;
  };

  // ─── File-Type Helpers (color-coded tile) ───
  const getFileExt = (filePath: string) => (filePath.split('.').pop() || '').toLowerCase();

  const getFileTypeMeta = (filePath: string) => {
    const ext = getFileExt(filePath);
    if (ext === 'pdf') {
      return {
        ext: 'PDF',
        icon: DocumentTextIcon,
        bgClass: 'bg-rose-500/15',
        borderClass: 'border-rose-500/40',
        textClass: 'text-rose-300',
        pillClass: 'text-rose-300 border-rose-500/30 bg-rose-500/10',
        isImage: false,
      };
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return {
        ext: ext.toUpperCase(),
        icon: PhotoIcon,
        bgClass: 'bg-pink-500/15',
        borderClass: 'border-pink-500/40',
        textClass: 'text-pink-300',
        pillClass: 'text-pink-300 border-pink-500/30 bg-pink-500/10',
        isImage: true,
      };
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return {
        ext: ext.toUpperCase(),
        icon: TableCellsIcon,
        bgClass: 'bg-emerald-500/15',
        borderClass: 'border-emerald-500/40',
        textClass: 'text-emerald-300',
        pillClass: 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10',
        isImage: false,
      };
    }
    if (['doc', 'docx'].includes(ext)) {
      return {
        ext: ext.toUpperCase(),
        icon: DocumentTextIcon,
        bgClass: 'bg-blue-500/15',
        borderClass: 'border-blue-500/40',
        textClass: 'text-blue-300',
        pillClass: 'text-blue-300 border-blue-500/30 bg-blue-500/10',
        isImage: false,
      };
    }
    return {
      ext: ext ? ext.toUpperCase() : 'FILE',
      icon: DocumentIcon,
      bgClass: 'bg-slate-500/15',
      borderClass: 'border-slate-500/40',
      textClass: 'text-slate-300',
      pillClass: 'text-slate-300 border-slate-500/30 bg-slate-500/10',
      isImage: false,
    };
  };

  // Initialen für Avatar
  const getInitials = (name?: string) => {
    if (!name) return '·';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // ─── Filter Sidebar Content (mobile sheet) ───
  const renderFilterContent = () => (
    <>
      {/* Dateityp */}
      <div className="mb-5">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">Dateityp</p>
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={filterType === 'all'} onChange={() => setFilterType('all')} className="w-3.5 h-3.5 rounded border-slate-600 text-primary-500 focus:ring-primary-500 bg-slate-800" />
            Alle ({documents.length})
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={filterType === 'pdf'} onChange={() => setFilterType(filterType === 'pdf' ? 'all' : 'pdf')} className="w-3.5 h-3.5 rounded border-slate-600 text-primary-500 focus:ring-primary-500 bg-slate-800" />
            PDF ({pdfCount})
          </label>
          <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300 cursor-pointer">
            <input type="checkbox" checked={filterType === 'image'} onChange={() => setFilterType(filterType === 'image' ? 'all' : 'image')} className="w-3.5 h-3.5 rounded border-slate-600 text-primary-500 focus:ring-primary-500 bg-slate-800" />
            Bilder ({imageCount})
          </label>
        </div>
      </div>

      {/* Tags / Kategorie */}
      {availableTags.length > 0 && (
        <div className="mb-5">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">Kategorie</p>
          <div className="space-y-1.5 max-h-36 overflow-y-auto">
            {availableTags.map(tag => (
              <label key={tag} className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterTags.includes(tag)}
                  onChange={() => handleFilterTagToggle(tag)}
                  className="w-3.5 h-3.5 rounded border-slate-600 text-primary-500 focus:ring-primary-500 bg-slate-800"
                />
                {tag} ({getTagCount(tag)})
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Zeitraum */}
      <div className="mb-5">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">Zeitraum</p>
        <div className="space-y-1.5">
          <input
            type="date"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="w-full px-2.5 py-2 text-xs rounded-xl text-gray-900 dark:text-white outline-none bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
            style={{ colorScheme: 'dark'  }}
            placeholder="Von"
          />
          <input
            type="date"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="w-full px-2.5 py-2 text-xs rounded-xl text-gray-900 dark:text-white outline-none bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
            style={{ colorScheme: 'dark'  }}
            placeholder="Bis"
          />
        </div>
      </div>

      {/* Uploader */}
      {uniqueCreators.length > 0 && (
        <div className="mb-5">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">Uploader</p>
          <div className="space-y-1.5">
            {uniqueCreators.map(creator => (
              <label key={creator} className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterCreator === creator}
                  onChange={() => setFilterCreator(filterCreator === creator ? 'all' : (creator || 'all'))}
                  className="w-3.5 h-3.5 rounded border-slate-600 text-primary-500 focus:ring-primary-500 bg-slate-800"
                />
                {creator} ({getCreatorCount(creator || '')})
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Sortierung */}
      <div className="mb-5">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">Sortierung</p>
        <div className="flex items-center gap-1.5">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="flex-1 px-2.5 py-2 text-xs rounded-xl text-gray-900 dark:text-white outline-none bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
            style={{ colorScheme: 'dark'  }}
          >
            <option value="created_at">Datum</option>
            <option value="name">Name</option>
            <option value="size">Größe</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="p-2 rounded-xl transition-colors bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
          >
            {sortOrder === 'asc' ? <ChevronUpIcon className="h-3.5 w-3.5 text-gray-600 dark:text-slate-400" /> : <ChevronDownIcon className="h-3.5 w-3.5 text-gray-600 dark:text-slate-400" />}
          </button>
        </div>
      </div>

      {/* Reset */}
      {hasActiveFilters && (
        <button
          onClick={clearAllFilters}
          className="w-full py-2 text-xs font-semibold text-gray-700 dark:text-slate-300 rounded-xl transition-colors bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
        >
          Filter zurücksetzen
        </button>
      )}
    </>
  );

  // ─── Document Card (Grid view) ───
  const renderDocumentCard = (doc: any) => {
    const meta = getFileTypeMeta(doc.file_path);
    const ext = getFileExt(doc.file_path);
    const Icon = meta.icon;
    const isImage = meta.isImage && ext !== 'svg';
    const isPdf = ext === 'pdf';
    const hasImageError = imageErrorStates.has(doc.id);
    const isLoadingImage = imageLoadingStates.has(doc.id);
    const hasLoadedImage = !isLoadingImage && !hasImageError;
    const isUncategorized = !doc.tag || doc.tag.trim() === '';
    const firstTag = doc.tag ? doc.tag.split(',').map((t: string) => t.trim()).filter(Boolean)[0] : null;
    const isSelected = selectedDocs.includes(doc.id);

    return (
      <article
        key={doc.id}
        onClick={() => isMergeMode ? toggleDocSelection(doc.id) : setSelectedDocId(doc.id)}
        className={`bg-white dark:bg-[#1c2432] ${`relative rounded-2xl overflow-hidden flex flex-col cursor-pointer transition-all`}`}
        style={{ border: isSelected
            ? '1px solid rgba(16,185,129,0.6)'
            : isUncategorized
              ? '1px solid rgba(245,158,11,0.40)'
              : '1px solid #334155', boxShadow: isSelected
            ? '0 0 0 2px rgba(16,185,129,0.30), 0 18px 40px -22px rgba(0,0,0,0.6)'
            : '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 18px 40px -22px rgba(0,0,0,0.55)' }}
        onMouseEnter={(e) => {
          if (!isSelected && !isUncategorized) {
            e.currentTarget.style.borderColor = 'rgba(16,185,129,0.35)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected && !isUncategorized) {
            e.currentTarget.style.borderColor = '#334155';
          }
        }}
      >
        {/* Selection Checkbox */}
        {isMergeMode && (
          <div className="absolute top-2 left-2 z-20">
            <div
              className={`w-5 h-5 rounded flex items-center justify-center ${isSelected ? 'bg-primary-500' : ''}`}
              style={!isSelected ? { background: 'rgba(15,23,42,0.8)', border: '1px solid #334155' } : undefined}
            >
              {isSelected && <CheckIcon className="h-3 w-3 text-slate-950" />}
            </div>
          </div>
        )}

        {/* Preview / Thumbnail */}
        <div className="relative aspect-[4/3] overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-slate-900/60" >
          {(isImage || isPdf) ? (
            <>
              {(hasImageError || (!hasLoadedImage && !isLoadingImage)) && (
                <div className={`w-14 h-16 rounded-lg ${meta.bgClass} border ${meta.borderClass} flex flex-col items-center justify-center`}>
                  <Icon className={`h-6 w-6 ${meta.textClass}`} />
                  <span className={`text-[8px] font-black ${meta.textClass} mt-1`}>{meta.ext}</span>
                </div>
              )}
              {!hasImageError && (
                <img
                  src={getSecureImageUrl(doc.id)}
                  alt={doc.name}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${hasLoadedImage && !isLoadingImage ? 'opacity-100' : 'opacity-0'}`}
                  onLoadStart={() => setImageLoadingStates(prev => new Set(prev).add(doc.id))}
                  onLoad={() => handleImageLoad(doc.id)}
                  onError={() => handleImageError(doc.id)}
                  loading="eager"
                  fetchPriority="high"
                />
              )}
            </>
          ) : (
            <div className={`w-14 h-16 rounded-lg ${meta.bgClass} border ${meta.borderClass} flex flex-col items-center justify-center`}>
              <Icon className={`h-6 w-6 ${meta.textClass}`} />
              <span className={`text-[8px] font-black ${meta.textClass} mt-1`}>{meta.ext}</span>
            </div>
          )}

          {/* Top-Right: Type pill */}
          {!isMergeMode && (
            <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
              {doc.linked_drinks_events && doc.linked_drinks_events.length > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-primary-700 dark:text-primary-300"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                  title={`Getränke: ${doc.linked_drinks_events.map((e: any) => e.beschriftung).join(', ')}`}
                >
                  Getränke
                </span>
              )}
              {(doc.is_linked === 1 || (doc.linked_bookings && doc.linked_bookings.length > 0)) && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-primary-700 dark:text-primary-300"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                  title="Verknüpft"
                >
                  <LinkIcon className="h-3 w-3" />
                  {doc.linked_bookings?.length > 0 && <span>{doc.linked_bookings.length}</span>}
                </span>
              )}
            </div>
          )}

          {/* Top-Left: Ungeordnet pill */}
          {isUncategorized && !isMergeMode && (
            <div className="absolute top-2 left-2 z-10">
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-amber-700 dark:text-amber-300"
                style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                Ungeordnet
              </span>
            </div>
          )}

          {/* Bottom-Left: Invoice */}
          {doc.invoice_amount && parseFloat(doc.invoice_amount) > 0 && (
            <div className="absolute bottom-2 left-2 z-10">
              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold text-slate-950 bg-primary-400">
                {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(parseFloat(doc.invoice_amount))}
              </span>
            </div>
          )}
          {/* Bottom-Right: Size */}
          {doc.size > 0 && (
            <div className="absolute bottom-2 right-2 z-10">
              <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold text-gray-900 dark:text-white" style={{ background: 'rgba(2,6,23,0.7)' }}>
                {formatSize(doc.size)}
              </span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 p-4 flex flex-col">
          <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate" title={doc.name}>
            {truncateText(doc.name, 40)}
          </h4>
          {doc.ai_comment && (
            <p className="text-[11px] text-gray-600 dark:text-slate-400 mt-1 line-clamp-2">
              {doc.ai_comment}
            </p>
          )}

          {/* Uploader + Date */}
          <div className="flex items-center gap-2 mt-3">
            <div className="w-6 h-6 rounded-full bg-primary-500/15 border border-primary-500/30 text-primary-700 dark:text-primary-300 font-bold text-[9px] flex items-center justify-center shrink-0">
              {getInitials(doc.member_name)}
            </div>
            <span className="text-[11px] text-gray-600 dark:text-slate-400 truncate flex-1 min-w-0">
              {doc.member_name || 'Unbekannt'}
            </span>
            <span className="text-[10px] text-gray-500 dark:text-slate-500 shrink-0">
              · {new Date(doc.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
            </span>
          </div>

          {/* AI Processing */}
          {processingDocId === doc.id && (
            <div className="text-[10px] text-primary-700 dark:text-primary-300 mt-2 flex items-center gap-1.5">
              <div className="w-3 h-3 border border-primary-400/40 border-t-primary-400 rounded-full animate-spin" />
              Analysiere…
            </div>
          )}

          {/* Footer: tag + actions */}
          {!isMergeMode && (
            <div className="mt-3 pt-3 flex items-center gap-1" style={{ borderTop: '1px solid #1e293b' }}>
              {firstTag ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-primary-700 dark:text-primary-300 mr-auto truncate max-w-[120px]"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                  title={firstTag}
                >
                  {firstTag}
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold mr-auto">Keine Kategorie</span>
              )}

              <button
                onClick={(e) => { e.stopPropagation(); setViewerDoc({ id: doc.id, name: doc.name, file_path: doc.file_path }); }}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 dark:text-slate-400 hover:bg-white/5 hover:text-white transition"
                title="Vorschau"
              >
                <EyeIcon className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); downloadDocument(doc.id, doc.name); }}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 dark:text-slate-400 hover:bg-white/5 hover:text-white transition"
                title="Download"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
              </button>
              {(!doc.is_linked && (!doc.linked_bookings || doc.linked_bookings.length === 0)) && (
                <button
                  onClick={(e) => { e.stopPropagation(); setQuickBookingDoc(doc); }}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 dark:text-slate-400 hover:bg-white/5 hover:text-white transition"
                  title="Buchen"
                >
                  <CurrencyEuroIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); sendToAI(doc.id); }}
                disabled={isAskingAI || processingDocId !== null}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-purple-300 hover:bg-purple-500/10 transition disabled:opacity-40"
                title="AI-Analyse"
              >
                <SparklesIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </article>
    );
  };

  // ─── Document List Row ───
  const renderDocumentListRow = (doc: any) => {
    const meta = getFileTypeMeta(doc.file_path);
    const ext = getFileExt(doc.file_path);
    const isImage = meta.isImage && ext !== 'svg';
    const isPdf = ext === 'pdf';
    const firstTag = doc.tag ? doc.tag.split(',').map((t: string) => t.trim()).filter(Boolean)[0] : null;
    const isSelected = selectedDocs.includes(doc.id);

    return (
      <div
        key={doc.id}
        onClick={() => isMergeMode ? toggleDocSelection(doc.id) : setSelectedDocId(doc.id)}
        className="px-4 sm:px-5 py-3 grid grid-cols-[40px_minmax(0,1fr)] md:grid-cols-[40px_minmax(0,2.5fr)_140px_180px_90px_70px_140px] gap-3 items-center cursor-pointer transition"
        style={{
          borderBottom: '1px solid #1e293b',
          background: isSelected ? 'rgba(16,185,129,0.06)' : 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.025)';
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.background = 'transparent';
        }}
      >
        {/* Selection / Thumb */}
        {isMergeMode ? (
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSelected ? 'bg-primary-500' : ''}`} style={!isSelected ? { background: 'rgba(15,23,42,0.6)', border: '1px solid #334155' } : undefined}>
            {isSelected && <CheckIcon className="h-4 w-4 text-slate-950" />}
          </div>
        ) : (
          <div className={`w-10 h-10 rounded-xl ${meta.bgClass} border ${meta.borderClass} flex items-center justify-center overflow-hidden`}>
            {(isImage || isPdf) && !imageErrorStates.has(doc.id) ? (
              <img
                src={getSecureImageUrl(doc.id)}
                alt=""
                className="w-10 h-10 object-cover"
                onError={() => handleImageError(doc.id)}
                loading="lazy"
              />
            ) : (
              <span className={`text-[9px] font-black ${meta.textClass}`}>{meta.ext}</span>
            )}
          </div>
        )}

        {/* Name + Description */}
        <div className="min-w-0">
          <div className="text-sm font-bold text-gray-900 dark:text-white truncate" title={doc.name}>{doc.name}</div>
          <div className="text-[11px] text-gray-600 dark:text-slate-400 truncate">
            {doc.ai_comment || `${ext.toUpperCase()} · ${doc.member_name || 'Unbekannt'} · ${formatSize(doc.size)}`}
          </div>
        </div>

        {/* Kategorie */}
        <div className="hidden md:block">
          {firstTag ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-primary-700 dark:text-primary-300"
              style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
            >
              {firstTag}
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-amber-700 dark:text-amber-300"
              style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              Ungeordnet
            </span>
          )}
        </div>

        {/* Uploader */}
        <div className="hidden md:flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-full bg-primary-500/15 border border-primary-500/30 text-primary-700 dark:text-primary-300 font-bold text-[9px] flex items-center justify-center shrink-0">
            {getInitials(doc.member_name)}
          </div>
          <span className="text-[11px] text-gray-700 dark:text-slate-300 truncate">{doc.member_name || 'Unbekannt'}</span>
        </div>

        {/* Datum */}
        <div className="hidden md:block text-[11px] text-gray-600 dark:text-slate-400 tabular-nums">
          {new Date(doc.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
        </div>

        {/* Größe */}
        <div className="hidden md:block text-[11px] text-gray-600 dark:text-slate-400 tabular-nums">{formatSize(doc.size)}</div>

        {/* Aktionen */}
        {!isMergeMode ? (
          <div className="hidden md:flex items-center justify-end gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); setViewerDoc({ id: doc.id, name: doc.name, file_path: doc.file_path }); }}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 dark:text-slate-400 hover:bg-white/5 hover:text-white transition"
              title="Vorschau"
            >
              <EyeIcon className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); downloadDocument(doc.id, doc.name); }}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-600 dark:text-slate-400 hover:bg-white/5 hover:text-white transition"
              title="Download"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); sendToAI(doc.id); }}
              disabled={isAskingAI || processingDocId !== null}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-purple-300 hover:bg-purple-500/10 transition disabled:opacity-40"
              title="AI-Analyse"
            >
              {processingDocId === doc.id ? (
                <div className="w-4 h-4 border-2 border-purple-300/40 border-t-purple-300 rounded-full animate-spin" />
              ) : (
                <SparklesIcon className="h-4 w-4" />
              )}
            </button>
            {(doc.is_linked === 1 || (doc.linked_bookings && doc.linked_bookings.length > 0)) && (
              <span title="Verknüpft" className="w-8 h-8 rounded-lg flex items-center justify-center text-primary-700 dark:text-primary-300">
                <LinkIcon className="h-4 w-4" />
              </span>
            )}
          </div>
        ) : (
          <div className="hidden md:block" />
        )}
      </div>
    );
  };

  // ─── Active Filter Chips ───
  const renderActiveFilterChips = () => {
    if (!hasActiveFilters && filterTags.length === 0) return null;
    return (
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Aktiv</span>
        {filterTags.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-primary-700 dark:text-primary-300"
            style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
          >
            {tag}
            <button onClick={() => handleRemoveFilterTag(tag)} className="text-primary-700 dark:text-primary-300 hover:text-primary-200">&times;</button>
          </span>
        ))}
        {filterType !== 'all' && (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
          >
            {filterType === 'pdf' ? 'PDF' : 'Bilder'}
            <button onClick={() => setFilterType('all')} className="text-gray-600 dark:text-slate-400 hover:text-white">&times;</button>
          </span>
        )}
        {filterCreator !== 'all' && (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
          >
            {filterCreator}
            <button onClick={() => setFilterCreator('all')} className="text-gray-600 dark:text-slate-400 hover:text-white">&times;</button>
          </span>
        )}
        {filterDateFrom && (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
          >
            Ab {new Date(filterDateFrom).toLocaleDateString('de-DE')}
            <button onClick={() => setFilterDateFrom('')} className="text-gray-600 dark:text-slate-400 hover:text-white">&times;</button>
          </span>
        )}
        {filterDateTo && (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
          >
            Bis {new Date(filterDateTo).toLocaleDateString('de-DE')}
            <button onClick={() => setFilterDateTo('')} className="text-gray-600 dark:text-slate-400 hover:text-white">&times;</button>
          </span>
        )}
        <span className="text-[10px] text-gray-500 dark:text-slate-500 ml-auto">{additionalFilteredDocs.length} Ergebnisse</span>
      </div>
    );
  };

  // ─── Kategorien-Strip Daten (aus availableTags + uncategorized) ───
  const categoryTiles = useMemo(() => {
    const colors = [
      { bg: 'bg-primary-500/10', border: 'border-primary-500/30', text: 'text-primary-300' },
      { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-300' },
      { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-300' },
      { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-300' },
      { bg: 'bg-pink-500/10', border: 'border-pink-500/30', text: 'text-pink-300' },
      { bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-300' },
    ];
    return availableTags.slice(0, 8).map((tag, idx) => ({
      tag,
      count: getTagCount(tag),
      color: colors[idx % colors.length],
    }));
  }, [availableTags, getTagCount]);

  return (
    <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">

        {/* ═══════════════════ INTRO ═══════════════════ */}
        <section className="pt-6 sm:pt-10 pb-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold mb-3">
            {documents.length} Dokumente · Stand {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
          </div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.02]" style={{ letterSpacing: '-0.04em' }}>
            Vereins-<br/><span className="text-amber-600 dark:text-amber-400">Akte.</span>
          </h2>
          <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
            <span className="font-bold text-gray-900 dark:text-white">{documents.length} Dokumente</span>
            {' · '}
            <span className="font-bold text-gray-900 dark:text-white">{thisWeekCount} diese Woche</span>
            {' · '}
            {availableTags.length > 0 && (
              <>
                <span className="font-bold text-gray-900 dark:text-white">{availableTags.length} Kategorien</span>
                {' · '}
              </>
            )}
            {uncategorizedCount > 0 ? (
              <span className="font-bold text-amber-700 dark:text-amber-300">{uncategorizedCount} ungeordnet</span>
            ) : (
              <span className="font-bold text-primary-700 dark:text-primary-300">alles geordnet</span>
            )}
            .
          </p>
        </section>

        {/* ═══════════════════ ACTION REQUIRED BANNER ═══════════════════ */}
        {uncategorizedCount > 0 && (
          <section className="py-3 sm:py-4">
            <article
              className="rounded-3xl p-5 md:p-6 bg-white dark:bg-[#1c2432]"
              style={{ border: '1px solid rgba(245,158,11,0.30)', boxShadow: '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div className="flex items-start gap-4 flex-wrap">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.40)' }}
                >
                  <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">Aufräumen erforderlich</div>
                  <h4 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                    {uncategorizedCount} unkategorisierte{uncategorizedCount === 1 ? 's Dokument' : ' Dokumente'}
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-slate-300 mt-2 max-w-2xl">
                    Diese Dateien wurden hochgeladen, aber noch keiner Kategorie zugeordnet
                    {uncategorizedDocs.slice(0, 3).length > 0 && (
                      <>
                        {' '}— z. B.{' '}
                        {uncategorizedDocs.slice(0, 3).map((d, i) => (
                          <React.Fragment key={d.id}>
                            <span className="font-semibold text-gray-900 dark:text-white">{d.name}</span>
                            {i < Math.min(2, uncategorizedDocs.length - 1) && ', '}
                          </React.Fragment>
                        ))}
                      </>
                    )}.
                  </p>
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-amber-700 dark:text-amber-300"
                      style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                      offen seit Upload
                    </span>
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-white/[0.08]"
                    >
                      {uncategorizedDocs.filter(d => /\.pdf$/i.test(d.file_path)).length} PDF · {uncategorizedDocs.filter(d => /\.(jpg|jpeg|png|gif|webp)$/i.test(d.file_path)).length} Bilder
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (uncategorizedDocs[0]) setSelectedDocId(uncategorizedDocs[0].id);
                  }}
                  className="px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold transition shrink-0"
                >
                  Jetzt ordnen
                </button>
              </div>
            </article>
          </section>
        )}

        {/* ═══════════════════ ERROR / SUCCESS MESSAGES ═══════════════════ */}
        {pdfError && (
          <div
            className="px-4 py-2.5 rounded-2xl text-sm flex items-center gap-2 mt-3"
            style={
              pdfError.includes('❌') || pdfError.includes('😓')
                ? { background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)', color: '#fda4af' }
                : { background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)', color: '#6ee7b7' }
            }
          >
            <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
            {pdfError}
          </div>
        )}

        {/* ═══════════════════ TOOLBAR ═══════════════════ */}
        {documents.length > 0 && (
          <section className="py-3 sm:py-4">
            <div
              className="rounded-3xl p-4 md:p-5 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
                {/* Search */}
                <div className="relative flex-1 min-w-0">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-600 dark:text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder={`Suche in ${documents.length} Dokumenten — Volltext, Tags, Uploader…`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full h-11 sm:h-12 pl-11 pr-12 rounded-2xl text-sm font-semibold text-gray-900 dark:text-white placeholder-slate-500 outline-none transition bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700"
                  />
                  {searchTerm ? (
                    <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-slate-400 hover:text-white">
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  ) : (
                    <kbd
                      className="hidden sm:inline-flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1 px-2 py-1 rounded-md text-[10px] text-gray-600 dark:text-slate-400 font-mono bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                    >
                      ⌘ K
                    </kbd>
                  )}
                </div>

                {/* Right cluster */}
                <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
                  {/* View toggle */}
                  <div className="flex items-center h-11 sm:h-12 rounded-2xl overflow-hidden border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/60">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`h-full px-3.5 text-xs font-semibold flex items-center gap-1.5 transition ${
                        viewMode === 'grid'
                          ? 'bg-primary-500/15 text-primary-700 dark:text-primary-300'
                          : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                      title="Kachelansicht"
                    >
                      <Squares2X2Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">Grid</span>
                    </button>
                    <div className="h-6 w-px bg-gray-200 dark:bg-slate-700" />
                    <button
                      onClick={() => setViewMode('list')}
                      className={`h-full px-3.5 text-xs font-semibold flex items-center gap-1.5 transition ${
                        viewMode === 'list'
                          ? 'bg-primary-500/15 text-primary-700 dark:text-primary-300'
                          : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                      title="Listenansicht"
                    >
                      <ListBulletIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">Liste</span>
                    </button>
                  </div>

                  {/* Mobile filter */}
                  <button
                    onClick={() => setShowMobileFilters(true)}
                    className="md:hidden relative h-11 sm:h-12 px-3.5 rounded-2xl text-xs font-semibold text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/60 hover:bg-gray-100 dark:hover:bg-slate-900/80 transition"
                  >
                    Filter
                    {activeFiltersCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary-500 text-slate-950 text-[9px] font-bold rounded-full flex items-center justify-center">{activeFiltersCount}</span>
                    )}
                  </button>

                  {user?.vorstand === 1 && (
                    <button
                      onClick={() => setShowAccessModal(true)}
                      className="hidden lg:inline-flex h-11 sm:h-12 px-3.5 rounded-2xl text-xs font-semibold text-gray-700 dark:text-slate-300 items-center gap-1.5 border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/60 hover:bg-gray-100 dark:hover:bg-slate-900/80 transition"
                      title="Dokumentenrechte"
                    >
                      <ShieldCheckIcon className="w-4 h-4" />
                      <span className="hidden xl:inline">Rechte</span>
                    </button>
                  )}

                  {/* Upload */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="h-11 sm:h-12 px-4 sm:px-5 rounded-2xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition flex items-center gap-1.5"
                  >
                    <CloudArrowUpIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">Upload</span>
                  </button>
                </div>
              </div>

              {/* Filter pills: PDF/Bilder Type-Filter (Kategorie-Tags entfernt) */}
              <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mr-1 shrink-0">Filter</span>
                <button
                  onClick={() => setFilterType('all')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold shrink-0 transition"
                  style={
                    filterType === 'all'
                      ? { background: '#34d399', borderColor: 'rgba(52,211,153,0.40)', border: '1px solid', color: '#020617' }
                      : { background: 'rgba(2,6,23,0.55)', border: '1px solid rgba(255,255,255,0.08)', color: '#cbd5e1' }
                  }
                >
                  Alle ({documents.length})
                </button>
                <button
                  onClick={() => setFilterType(filterType === 'pdf' ? 'all' : 'pdf')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold shrink-0 transition"
                  style={
                    filterType === 'pdf'
                      ? { background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)', color: '#fda4af' }
                      : { background: 'rgba(2,6,23,0.55)', border: '1px solid rgba(255,255,255,0.08)', color: '#cbd5e1' }
                  }
                >
                  PDF ({pdfCount})
                </button>
                <button
                  onClick={() => setFilterType(filterType === 'image' ? 'all' : 'image')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold shrink-0 transition"
                  style={
                    filterType === 'image'
                      ? { background: 'rgba(236,72,153,0.10)', border: '1px solid rgba(236,72,153,0.30)', color: '#f9a8d4' }
                      : { background: 'rgba(2,6,23,0.55)', border: '1px solid rgba(255,255,255,0.08)', color: '#cbd5e1' }
                  }
                >
                  Bilder ({imageCount})
                </button>
              </div>
            </div>
          </section>
        )}


        {/* ═══════════════════ MAIN CONTENT ═══════════════════ */}
        {documents.length === 0 ? (
          <section className="py-10">
            <div
              className="rounded-3xl p-8 sm:p-12 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow: '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div className="w-20 h-20 rounded-2xl bg-primary-500/10 border border-primary-500/30 flex items-center justify-center mx-auto mb-6">
                <DocumentTextIcon className="h-10 w-10 text-primary-700 dark:text-primary-300" />
              </div>
              <h2 className="text-xl font-black text-gray-900 dark:text-white mb-2">Keine Dokumente</h2>
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-8 max-w-md mx-auto">Lade dein erstes Dokument hoch, um zu starten — Verträge, Protokolle, Bilder oder Rechnungen.</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-primary-500 hover:bg-primary-400 text-slate-950 font-bold text-sm transition"
              >
                <CloudArrowUpIcon className="h-5 w-5" /> Dokumente hochladen
              </button>
            </div>
          </section>
        ) : (
          <>
            {/* Active Filters */}
            <section className="pt-2 sm:pt-4">
              {renderActiveFilterChips()}
            </section>

            {/* Library Header */}
            <section className="pb-4 sm:pb-5">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-primary-400/80 font-semibold">
                    Bibliothek · {viewMode === 'grid' ? 'Grid-Ansicht' : 'Listen-Ansicht'}
                  </div>
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
                    {additionalFilteredDocs.length} {additionalFilteredDocs.length === 1 ? 'Dokument' : 'Dokumente'}
                  </h3>
                </div>
                {totalPages > 1 && (
                  <span
                    className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-white/[0.08]"
                  >
                    Seite {currentPage} / {totalPages}
                  </span>
                )}
              </div>
            </section>

            {/* Document Grid / List */}
            {additionalFilteredDocs.length === 0 ? (
              <section className="py-12 text-center">
                <MagnifyingGlassIcon className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-gray-600 dark:text-slate-400">Keine Dokumente gefunden</p>
                {hasActiveFilters && (
                  <button onClick={clearAllFilters} className="mt-2 text-xs text-primary-700 dark:text-primary-300 hover:text-primary-200 font-semibold">
                    Filter zurücksetzen
                  </button>
                )}
              </section>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {paginatedDocs.map((doc) => renderDocumentCard(doc))}
              </div>
            ) : (
              <div
                className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                style={{ boxShadow: '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)' }}
              >
                {/* Header */}
                <div className="hidden md:grid grid-cols-[40px_minmax(0,2.5fr)_140px_180px_90px_70px_140px] gap-3 px-5 py-3 text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold" style={{ borderBottom: '1px solid #1e293b' }}>
                  <div></div>
                  <div>Datei</div>
                  <div>Kategorie</div>
                  <div>Uploader</div>
                  <div>Datum</div>
                  <div>Größe</div>
                  <div className="text-right">Aktionen</div>
                </div>
                {paginatedDocs.map((doc) => renderDocumentListRow(doc))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 rounded-xl text-xs font-semibold text-gray-700 dark:text-slate-300 transition disabled:opacity-40 disabled:cursor-not-allowed bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  Zurück
                </button>
                {(() => {
                  const pages: (number | 'gap')[] = [];
                  if (totalPages <= 7) {
                    for (let p = 1; p <= totalPages; p++) pages.push(p);
                  } else if (currentPage <= 4) {
                    pages.push(1, 2, 3, 4, 5, 'gap', totalPages);
                  } else if (currentPage >= totalPages - 3) {
                    pages.push(1, 'gap', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                  } else {
                    pages.push(1, 'gap', currentPage - 1, currentPage, currentPage + 1, 'gap', totalPages);
                  }
                  return pages.map((p, i) => {
                    if (p === 'gap') {
                      return (
                        <span key={`gap-${i}`} className="w-9 h-9 flex items-center justify-center text-xs font-bold text-gray-400 dark:text-slate-500 select-none">
                          …
                        </span>
                      );
                    }
                    const isActive = currentPage === p;
                    return (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`w-9 h-9 rounded-xl text-xs font-bold transition ${
                          isActive
                            ? 'bg-primary-500 text-slate-950'
                            : 'bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-white/10'
                        }`}
                      >
                        {p}
                      </button>
                    );
                  });
                })()}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 rounded-xl text-xs font-semibold text-gray-700 dark:text-slate-300 transition disabled:opacity-40 disabled:cursor-not-allowed bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  Weiter
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══════════════════ BULK-ACTION BAR ═══════════════════ */}
      {selectedDocs.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 px-4 w-full max-w-2xl">
          <div
            className="rounded-2xl px-4 py-3 flex items-center gap-3 flex-wrap bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)' }}
          >
            <div className="flex items-center gap-2 mr-auto">
              <div className="w-8 h-8 rounded-lg bg-primary-500/15 border border-primary-500/30 text-primary-700 dark:text-primary-300 font-bold text-xs flex items-center justify-center">
                {selectedDocs.length}
              </div>
              <div className="text-sm text-gray-700 dark:text-slate-300">
                <span className="font-bold text-gray-900 dark:text-white">{selectedDocs.length}</span> ausgewählt
              </div>
            </div>
            <button
              onClick={handleMerge}
              disabled={selectedDocs.length < 2}
              className="px-3 py-2 rounded-xl text-xs font-semibold text-blue-700 dark:text-blue-300 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)' }}
            >
              <DocumentPlusIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Zusammenführen</span>
            </button>
            <button
              onClick={() => { setDocsToDelete(selectedDocs); setShowDeleteConfirm(true); }}
              className="px-3 py-2 rounded-xl text-xs font-semibold text-rose-700 dark:text-rose-300 transition flex items-center gap-1.5"
              style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
            >
              <TrashIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Löschen</span>
            </button>
            <button
              onClick={() => setSelectedDocs([])}
              className="px-3 py-2 rounded-xl text-xs font-semibold text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════ MOBILE FILTER SHEET ═══════════════════ */}
      {showMobileFilters && (
        <>
          <div
            className="fixed inset-0 z-50"
            style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={() => setShowMobileFilters(false)}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl max-h-[85vh] flex flex-col bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{ boxShadow: '0 -30px 60px -20px rgba(0,0,0,0.7)' }}
          >
            <div className="flex justify-center py-2">
              <div className="w-10 h-1 bg-slate-700 rounded-full" />
            </div>
            <div className="px-5 pb-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Filter</div>
                <h3 className="text-lg font-black text-gray-900 dark:text-white tracking-tight mt-0.5">Verfeinern</h3>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-gray-600 dark:text-slate-400">{additionalFilteredDocs.length} Treffer</span>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="w-9 h-9 rounded-full flex items-center justify-center bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-4">
              {renderFilterContent()}
            </div>
            <div className="px-5 py-4" style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="w-full py-3 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 font-bold text-sm transition"
              >
                Anwenden
              </button>
            </div>
          </div>
        </>
      )}

      {/* ═══════════════════ MODALS ═══════════════════ */}

      {/* Document Detail Modal */}
      {selectedDocId && (
        <DocumentDetailModal
          documentId={selectedDocId}
          onClose={() => setSelectedDocId(null)}
          onDelete={(id) => { handleDeleteDocument(id); setSelectedDocId(null); }}
          onTagAdd={handleAddTag}
          onTagDelete={handleDeleteTag}
          onAIScan={sendToAI}
          onDownload={downloadDocument}
          onDocumentUpdated={() => setThumbnailVersion(Date.now())}
          availableTags={availableTags}
        />
      )}

      {/* Document Viewer (Fullscreen) */}
      {viewerDoc && (
        <DocumentViewer
          documentId={viewerDoc.id}
          fileName={viewerDoc.name}
          filePath={viewerDoc.file_path}
          onClose={() => setViewerDoc(null)}
          onDocumentUpdated={() => setThumbnailVersion(Date.now())}
        />
      )}

      {/* Quick Booking Modal */}
      <QuickBookingModal
        isOpen={!!quickBookingDoc}
        onClose={() => setQuickBookingDoc(null)}
        onSuccess={() => { fetchDocuments(); setQuickBookingDoc(null); }}
        document={quickBookingDoc}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)' }}
          >
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}>
              <ExclamationTriangleIcon className="h-7 w-7 text-rose-600 dark:text-rose-400" />
            </div>
            <h3 className="text-lg font-black text-gray-900 dark:text-white text-center mb-2">{docsToDelete.length} Dokument(e) löschen?</h3>
            <p className="text-gray-600 dark:text-slate-400 text-center mb-6 text-sm">Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDocsToDelete([]); }}
                className="flex-1 py-2.5 rounded-xl text-gray-800 dark:text-slate-200 font-semibold text-sm bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-2.5 rounded-xl bg-rose-500 hover:bg-rose-400 text-gray-900 dark:text-white font-bold text-sm"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(2,6,23,0.75)', backdropFilter: 'blur(8px)' }}
        >
          <div
            className="rounded-3xl p-8 max-w-sm w-full mx-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)' }}
          >
            <div className="flex flex-col items-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 animate-ping"><CloudArrowUpIcon className="h-16 w-16 text-primary-600 dark:text-primary-400 opacity-40" /></div>
                <CloudArrowUpIcon className="h-16 w-16 text-primary-600 dark:text-primary-400 relative animate-bounce" />
              </div>
              <h3 className="text-lg font-black text-gray-900 dark:text-white mb-2">Hochladen…</h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
                {uploadProgress.total > 1 ? `${uploadProgress.total} Dokumente` : '1 Dokument'}
              </p>
              <div className="w-full rounded-full h-2 overflow-hidden bg-gray-50 dark:bg-slate-900/60" >
                <div className="h-full bg-primary-500 rounded-full animate-pulse" style={{ width: '100%' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant || 'single'}
        showCloseButton={true}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => { if (confirmModal.onConfirm) confirmModal.onConfirm(); setConfirmModal({ ...confirmModal, isOpen: false }); }
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: { text: 'Abbrechen', onPress: () => setConfirmModal({ ...confirmModal, isOpen: false }) },
          confirm: { text: 'Bestätigen', onPress: () => { if (confirmModal.onConfirm) confirmModal.onConfirm(); setConfirmModal({ ...confirmModal, isOpen: false }); } }
        } : undefined}
      />

      {/* Document Access Modal */}
      <DocumentAccessModal isOpen={showAccessModal} onClose={() => setShowAccessModal(false)} />
    </div>
  );
};

export default DocumentManagement;
